import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { PermissionAction, ModulePermission } from '../types/permissions';

export const usePermissions = () => {
    const authContext = useContext(AuthContext);

    if (!authContext) {
        throw new Error('usePermissions must be used within an AuthProvider');
    }

    const { user } = authContext;

    // Type guard to check if user is a CRM user (not a customer)
    const isCRMUser = (user: any): user is import('../models/user').User => {
        return user && user.user_type === 'crm';
    };

    const hasPermission = (module: ModulePermission, action: PermissionAction): boolean => {
        if (!isCRMUser(user) || !user?.role?.permissions) return false;

        // Admin users have all permissions
        if (user.is_tenant_manager) {
            return true;
        }

        // Check specific permission - frontend only checks module + action
        const modulePermissions = user?.role?.permissions[module];

        // Return false if module doesn't exist
        if (!modulePermissions) {
            return false;
        }

        // Check if the action exists and has any scope enabled
        const actionPermissions = modulePermissions[action as keyof typeof modulePermissions];

        if (!actionPermissions) {
            return false;
        }

        // If actionPermissions is an object with scope properties, check if any scope is enabled
        if (typeof actionPermissions === 'object' && actionPermissions !== null) {
            return (
                (actionPermissions as any).mine === true ||
                (actionPermissions as any).my_reports === true ||
                (actionPermissions as any).my_team === true ||
                (actionPermissions as any).all === true
            );
        }

        // Fallback to Boolean conversion
        return Boolean(actionPermissions);
    };

    const hasAnyPermission = (
        permissions: Array<{ module: ModulePermission; action: PermissionAction }>
    ): boolean => {
        return permissions.some(({ module, action }) => hasPermission(module, action));
    };

    const hasAllPermissions = (
        permissions: Array<{ module: ModulePermission; action: PermissionAction }>
    ): boolean => {
        return permissions.every(({ module, action }) => hasPermission(module, action));
    };

    const getPermissionDetails = (
        module: ModulePermission,
        action: PermissionAction
    ): { mine: boolean; my_reports: boolean; my_team: boolean; all: boolean } | null => {
        if (!isCRMUser(user) || !user?.role?.permissions) return null;

        const modulePermissions = user?.role?.permissions[module];

        if (!modulePermissions) {
            return null;
        }

        const actionPermissions = modulePermissions[action as keyof typeof modulePermissions];

        // Only return object types, not strings
        if (typeof actionPermissions === 'object' && actionPermissions !== null) {
            return {
                mine: (actionPermissions as any).mine === true,
                my_reports: (actionPermissions as any).my_reports === true,
                my_team: (actionPermissions as any).my_team === true,
                all: (actionPermissions as any).all === true,
            };
        }

        return null;
    };

    const getModulePermissions = (module: ModulePermission): Record<string, any> => {
        if (!isCRMUser(user) || !user?.role?.permissions) return {};
        return user?.role?.permissions[module] || {};
    };

    return {
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        getPermissionDetails,
        getModulePermissions,
        isAdmin: isCRMUser(user) && user.is_tenant_manager,
        isCRM: isCRMUser(user),
        isUser: isCRMUser(user),
    };
};
